import { escapeAttr } from "./linkPreview";

/**
 * The browser's Application Insights connection string, set at runtime (issue #851).
 *
 * It used to be compiled into the bundle (`VITE_APP_INSIGHTS_CONNECTION_STRING`), so an image
 * built for dev sent QA's and production's browser telemetry to dev. Now the web writes it into
 * the document it serves, as one `<meta>` tag, from its own `APPLICATIONINSIGHTS_CONNECTION_STRING`
 * and only when `telemetryTarget` (`server/telemetryTarget.ts`) says this process sends telemetry
 * (deployed, or `SKYDATA_LOCAL_TELEMETRY=1`). One image digest then reports to whichever
 * environment runs it.
 *
 * A meta tag rather than a field of `GET /api/auth/config`: the logger is initialised by the
 * first import of `main.tsx`, synchronously, before anything can log, and the browser SDK cannot
 * be pointed elsewhere after `initLogger` runs. A tag is in the document before any script, needs
 * no request, and is not inline script, so it needs no CSP allowance.
 *
 * Public by nature, never a secret: every single-page app ships this value to every visitor, and
 * its key can only write telemetry to that one component (s3-decisions.md #27). It is still never
 * logged (`SECRET_KEYS` in `server/config.ts`).
 */

/** The `name` of the tag. The client reads it in `client/src/lib/logger.ts`. */
export const BROWSER_TELEMETRY_META = "skydata-appinsights-connection-string";

/** The document with the tag added before `</head>`, or unchanged when telemetry is off. */
export function injectBrowserTelemetry(html: string, connectionString: string | undefined): string {
  const value = connectionString?.trim();
  if (!value) return html;
  const tag = `<meta name="${BROWSER_TELEMETRY_META}" content="${escapeAttr(value)}" />`;
  return html.replace("</head>", () => `    ${tag}\n  </head>`);
}

/** The connection string the served document carries, or undefined (telemetry off). */
export function readBrowserTelemetry(doc: Document | undefined): string | undefined {
  const content = doc
    ?.querySelector<HTMLMetaElement>(`meta[name="${BROWSER_TELEMETRY_META}"]`)
    ?.getAttribute("content")
    ?.trim();
  return content ? content : undefined;
}
